import { CompleteJson } from "../types/DealTracker";
import CompanyNameCellRenderer from "../components/DealTracker/CompanyNameCellRenderer";
import FlowCellRenderer from "../components/DealTracker/FlowCellRenderer";

import PayableCellRenderer from "../components/DealTracker/PayableCell/PayableCellRenderer";
import PayablesEditor from "../components/DealTracker/PayableCell/PayablesEditor";
import ReceivablesCellRenderer from "../components/DealTracker/ReceivablesCell/ReceivablesCellRenderer";
import ReceivablesEditor from "../components/DealTracker/ReceivablesCell/ReceivablesEditor";
import FileCellRenderer from "../components/DealTracker/FileCellRenderer";
import LogisticsProviderCellEditor from "../components/DealTracker/LogisticsProviderCellEditor";
import { ICellRendererParams } from "ag-grid-community";
import ETACellRenderer from "../components/DealTracker/ETACellRenderer";
import AIAutofillCellRenderer from "../components/DealTracker/AIAutofillCellRenderer";

export type ColConfig = Partial<import("ag-grid-community").ColDef>;

export const generateColumnDefs = (row: CompleteJson): ColConfig[] => {
  if (!row) return [];

  return Object.entries(row).map(([field, cell]) => {
    const type = cell?.column_type?.data_type;
    const info = cell?.column_type?.additional_info;
    const options = cell?.column_type?.options;

    return makeColDef(field, type, options);
  });
};

export const inferDataType = (value: any) => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return "date";
  }

  return "string";
};

export const cloneInitialRow = (row: CompleteJson): CompleteJson => {
  const result: CompleteJson = {} as CompleteJson;

  (Object.keys(row) as (keyof CompleteJson)[]).forEach((key) => {
    const val = row[key];
    result[key] =
      typeof val === "object" && val !== null ? ({ ...val } as any) : val;
  });

  return result;
};

// utility function that always applies custom renderer rules
export function makeColDef(
  field: string,
  data_type: string,
  options: string[] = [],
) {
  const baseColDef: ColConfig = {
    field,
    headerName: field
      .split("_")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" "),
  };

  if (field === "company_name") {
    baseColDef.cellRenderer = CompanyNameCellRenderer;
    baseColDef.pinned = true;
  }

  if (field === "eta") {
    baseColDef.headerName = field.toUpperCase();
    baseColDef.editable = false;
    baseColDef.cellStyle = { backgroundColor: "#f3f4f6" };
    baseColDef.cellRenderer = ETACellRenderer;
  }

  if (field === "flow") {
    baseColDef.cellRenderer = FlowCellRenderer;
    baseColDef.editable = false;
  }

  if (field === "logistics_provider") {
    baseColDef.cellRenderer = (props: ICellRendererParams) => props.value;
    baseColDef.cellEditor = LogisticsProviderCellEditor;
  }

  if (field === "row_id") {
    baseColDef.hide = true;
  }

  if (field === "payables_status") {
    baseColDef.hide = true;
  }

  if (field === "payables") {
    baseColDef.cellRenderer = PayableCellRenderer;
    baseColDef.cellEditor = PayablesEditor;
  }

  if (field === "receivables_status") {
    baseColDef.hide = true;
  }

  if (field === "receivables") {
    baseColDef.cellRenderer = ReceivablesCellRenderer;
    baseColDef.cellEditor = ReceivablesEditor;
  }

  // Handle date formatting
  if (data_type === "date") {
    baseColDef.cellDataType = "date";
    baseColDef.valueFormatter = (params) => {
      const value = params.value;
      return value
        ? new Intl.DateTimeFormat("en-IN", {
            year: "numeric",
            month: "short",
            day: "2-digit",
          })?.format(new Date(value))
        : "";
    };
  }

  // Handle select fields dynamically
  if (data_type === "select" && field !== "flow" && options) {
    baseColDef.cellEditor = "agSelectCellEditor";
    baseColDef.cellEditorParams = { values: options };

    // Optionally, add colored cellStyle for known fields
    if (field === "order_status") {
      baseColDef.cellStyle = (params) => {
        switch (params.value) {
          case "Order Placed":
            return { backgroundColor: "#FF914D", fontWeight: "500" };
          case "In Transit":
            return { backgroundColor: "#FFDC32", fontWeight: "500" };
          case "Received":
            return { backgroundColor: "#17B26A", fontWeight: "500" };
          default:
            return null;
        }
      };
    }
  }

  if (data_type === "file") {
    baseColDef.cellRenderer = FileCellRenderer;
    baseColDef.editable = false; // optional, or true if you want an editor
  }

  if (data_type === "file" && field === "upload_to_autofill") {
    baseColDef.cellRenderer = AIAutofillCellRenderer;
    baseColDef.editable = false; // optional, or true if you want an editor
  }

  // Merge in hard-coded overrides if they exist
  // const override = specialColumnOverrides[field] ?? {};

  return {
    ...baseColDef,
    // ...override,
  };
}
